import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { getRecommendedSize } from './size-guide/sizeRecommendation';
import { useContext } from "react";
import SaleContext from "./context/SaleContext";
import { getSalePrice } from "./utils/pricing";

const Product = ({ product, addToCart, isFirstProduct, isGalleryView }) => {
  const { sale } = useContext(SaleContext);
  const salePrice = getSalePrice(product.price, sale);

  const isSaleActive =
    Boolean(sale) &&
    salePrice < product.price;
  const [selectedSize, setSelectedSize] = useState('');
  const [notification, setNotification] = useState('');
  const [recommendedSize, setRecommendedSize] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [reviews, setReviews] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({
    average: 0,
    count: 0
  });

  useEffect(() => {
    setIsExpanded(!isGalleryView);
  }, [isGalleryView]);

  useEffect(() => {
    const measurements = JSON.parse(localStorage.getItem("shushMeasurement"));
    const recSize = getRecommendedSize(measurements, product.sizes);
    setRecommendedSize(recSize);
  }, [product]);

  useEffect(() => {
    if (!product) return;

    const fetchReviews = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reviews/${product._id}`);
        const data = await res.json();

        setReviews(data);

        if (data.length > 0) {
          const avg =
            data.reduce((sum, r) => sum + r.rating, 0) / data.length;

          setRatingSummary({
            average: avg,
            count: data.length
          });
        } else {
          setRatingSummary({ average: 0, count: 0 });
        }
      } catch (error) {
        console.error("Failed to fetch reviews");
      }
    };

    fetchReviews();
  }, [product]);

  const scrollRef = useRef(null);

  const handleScroll = () => {
    if (!scrollRef.current) return;

    const scrollLeft = scrollRef.current.scrollLeft;
    const width = scrollRef.current.clientWidth;

    const index = Math.round(scrollLeft / width);
    setActiveIndex(index);
    setHasInteracted(true);
  };


  const handleSizeChange = (event) => {
    setSelectedSize(event.target.value);
  };

  const handleAddToCart = () => {
    if (selectedSize) {
      addToCart({ ...product, selectedSize });
      setNotification("✓ Added to cart!");
      setTimeout(() => setNotification(""), 2000); // clear after 2s
    } else {
      setNotification("⚠️ Please select a size.");
      setTimeout(() => setNotification(""), 2000);
    }
  };

  const getConfidenceCopy = (confidence) => {
    switch (confidence) {
      case "High":
        return "Best fit match";
      case "Medium":
        return "Likely Fit";
      case "Low":
        return "Based on your fit";
      default:
        return "";
    }
  };

  const images = [
    product.images?.main,
    product.images?.side,
    product.images?.top
  ].filter(Boolean).slice(0, 3);

  const renderStars = (rating) => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        stars.push(<FaStar key={i} />);
      } else if (rating >= i - 0.5) {
        stars.push(<FaStarHalfAlt key={i} />);
      } else {
        stars.push(<FaRegStar key={i} />);
      }
    }

    return stars;
  };


  return (
    <div className="shop-product-card">
      {/* Recommended size box */}
      <div className="fit-line"
      style={{
          maxHeight: isExpanded ? '500px' : '0',
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
          transition: 'all 0.4s ease-in-out',
        }}>
        {!recommendedSize ? (
          <Link to="/size-guide" className="fit-link">
            Find your size →
          </Link>
        ) : recommendedSize.message === "Size not available" ? (
          <span className="size-unavailable">Size not available</span>
        ) : (
          <span>
            Best fit: <strong>{recommendedSize.bestFit}</strong>
            {recommendedSize.confidence && (
              <span className="fit-confidence">
                {" "}· {getConfidenceCopy(recommendedSize.confidence)}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="product-image-wrapper" onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
          {product.topPick && (
        <div className="bestseller-badge">
          Bestseller
        </div>
      )}
      {sale && (
        <div className="sale-badge">
        {sale.badgeText}
        </div>
        )}
      <div className="product-image-scroll" ref={scrollRef} onScroll={handleScroll}>
          {images.map((img, index) => (
            <Link
              key={index}
              to={`/product/${product._id}`}
              className="product-image-slide"
            >
              <img
                src={img}
                alt={product.name}
                loading={isFirstProduct && index === 0 ? "eager" : "lazy"}
                fetchPriority={isFirstProduct && index === 0 ? "high" : "auto"}
                decoding="async"
               />
            </Link>
          ))}
        </div>
        <div className="image-dots">
          {images.map((_, i) => (
            <div
              key={i}
              className={`image-dot ${i === activeIndex ? "active" : ""}`}
            />
          ))}
        </div>
        {!hasInteracted && (
          <div className="swipe-hint">Swipe →</div>
        )}
      </div>

      <div className="shop-product-meta"
      style={{
          maxHeight: isExpanded ? '500px' : '0',
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
          transition: 'all 0.4s ease-in-out',
        }}>
        <h3 className="shop-product-collection">{product.brand}</h3>
        <h3 className="shop-product-name">in {product.name}</h3>

        <div className="product-title-row">
        <div className="shop-product-price-container">
          {isSaleActive ? (
            <>
              <p className="shop-product-original-price">
                ₹{product.price}
              </p>

              <p className="shop-product-sale-price">
                ₹{salePrice}
              </p>
            </>
          ) : (
            <p className="shop-product-price">
              ₹{product.price}
            </p>
          )}
          </div>
          {ratingSummary.count > 0 && (
            <div className="product-rating-preview">
              <div className="stars">
                {renderStars(ratingSummary.average)}
              </div>
              <span className="rating-count">
                ({ratingSummary.count})
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="size-cart-row"
      style={{
          maxHeight: isExpanded ? '500px' : '0',
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
          transition: 'all 0.4s ease-in-out',
        }}>
        <div className="size-select-container">
          <select
            id={`size-${product._id}`}
            value={selectedSize}
            onChange={handleSizeChange}
            className="size-select"
          >
            <option value="">Select Size</option>
            {product.sizes.map((sizeObj) => (
              <option
                key={sizeObj._id}
                value={sizeObj.size}
                disabled={sizeObj.stock <= 0}
              >
                {sizeObj.size}{sizeObj.stock <= 0 ? " (Out of stock)" : ""}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleAddToCart}
          className="product-add-to-cart-button"
        >
          Add to Cart
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className="notification">
          {notification}
        </div>
      )}
    </div>
  );
};

export default Product;
